import { useEffect, useState } from "react";
import { Cpu, KeyRound, MailWarning, Moon, Play, Send, Square, Sun } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Field, NSelect, PageHeader } from "@/components/common";
import { useTheme } from "@/context/ThemeContext";
import { useAuth } from "@/context/AuthContext";
import { useFleet } from "@/context/FleetContext";
import { api, errMsg, API_BASE } from "@/lib/api";
import { cn } from "@/lib/utils";
import { InstallAppCard, PushCard } from "@/components/MobileApp";

function Card({ title, icon: Icon, children, testId }) {
  return (
    <div className="gt-card p-5" data-testid={testId}>
      <div className="mb-4 flex items-center gap-2 text-sm font-bold"><Icon className="h-4 w-4 gt-brand-text" /> {title}</div>
      {children}
    </div>
  );
}

function PasswordCard() {
  const [f, setF] = useState({ current_password: "", new_password: "" });
  const save = async () => {
    try {
      await api.post("/auth/change-password", f);
      toast.success("Password berhasil diubah");
      setF({ current_password: "", new_password: "" });
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Card title="Keamanan Akun" icon={KeyRound} testId="settings-password-card">
      <div className="space-y-3">
        <Field label="Password saat ini"><input type="password" className="gt-input" value={f.current_password} onChange={(e) => setF({ ...f, current_password: e.target.value })} data-testid="settings-current-password" /></Field>
        <Field label="Password baru (min 8 karakter)"><input type="password" className="gt-input" value={f.new_password} onChange={(e) => setF({ ...f, new_password: e.target.value })} data-testid="settings-new-password" /></Field>
        <button onClick={save} disabled={!f.current_password || f.new_password.length < 8} className="rounded-xl bg-primary px-4 py-2.5 text-sm font-bold text-primary-foreground disabled:opacity-50" data-testid="settings-password-save">Ubah Password</button>
      </div>
    </Card>
  );
}

function EmailAlertCard() {
  const { user } = useAuth();
  const [on, setOn] = useState(user?.email_alerts !== false);
  const save = async (v) => {
    try { await api.put("/auth/preferences", { email_alerts: v }); setOn(v); toast.success(v ? "Email alarm aktif" : "Email alarm dimatikan"); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Card title="Email Alarm" icon={MailWarning} testId="settings-email-card">
      <div className="flex items-center justify-between gap-4">
        <div>
          <div className="text-sm font-semibold" data-testid="email-alert-status">{on ? "Aktif" : "Nonaktif"} · {user?.email}</div>
          <div className="text-xs text-muted-foreground">Email dikirim saat alarm kritis: kendaraan bergerak tanpa ACC atau power perangkat terputus (maks. 1 email per 30 menit per jenis alarm).</div>
        </div>
        <Switch checked={on} onCheckedChange={save} data-testid="email-alert-toggle" />
      </div>
    </Card>
  );
}

function SimulatorCard() {
  const { vehicles } = useFleet();
  const [st, setSt] = useState(null);
  const [m, setM] = useState({ imei: "", lat: -6.2146, lng: 106.818, speed: 45, direction: 90, acc: true });
  const load = () => api.get("/dev/simulator").then((r) => setSt(r.data));
  useEffect(() => { load(); const iv = setInterval(load, 5000); return () => clearInterval(iv); }, []);
  useEffect(() => { if (!m.imei && vehicles[0]) setM((x) => ({ ...x, imei: vehicles[0].imei })); }, [vehicles, m.imei]);
  const act = async (a) => { try { const { data } = await api.post(`/dev/simulator/${a}`); setSt({ ...st, ...data }); toast.success(a === "start" ? "Simulator berjalan" : "Simulator dihentikan"); } catch (e) { toast.error(errMsg(e)); } };
  const send = async () => {
    try { await api.post("/dev/simulator/send", { ...m, lat: Number(m.lat), lng: Number(m.lng), speed: Number(m.speed), direction: Number(m.direction) }); toast.success("Posisi terkirim via pipeline decoder → database → WebSocket"); } catch (e) { toast.error(errMsg(e)); }
  };
  if (!st) return null;
  return (
    <Card title="GPS Simulator · Development Mode" icon={Cpu} testId="settings-simulator-card">
      {!st.enabled ? <div className="text-sm text-muted-foreground">Simulator dinonaktifkan di environment ini (production).</div> : (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            <span className={cn("flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-bold", st.running ? "border-emerald-500/40 text-emerald-500" : "border-border text-muted-foreground")} data-testid="simulator-status">
              <span className={cn("h-1.5 w-1.5 rounded-full", st.running ? "bg-emerald-500 gt-blink" : "bg-slate-500")} />{st.running ? "BERJALAN" : "BERHENTI"}
            </span>
            <span className="font-mono text-xs text-muted-foreground">{st.positions_sent} posisi terkirim · tiap {st.tick_seconds}s</span>
            <div className="ml-auto flex gap-2">
              <button onClick={() => act("start")} disabled={st.running} className="flex items-center gap-1.5 rounded-xl bg-primary px-3 py-2 text-xs font-bold text-primary-foreground disabled:opacity-40" data-testid="simulator-start"><Play className="h-3.5 w-3.5" /> Simulasikan Pergerakan</button>
              <button onClick={() => act("stop")} disabled={!st.running} className="flex items-center gap-1.5 rounded-xl border border-border px-3 py-2 text-xs font-bold disabled:opacity-40" data-testid="simulator-stop"><Square className="h-3.5 w-3.5" /> Stop</button>
            </div>
          </div>
          <div className="rounded-xl border border-dashed border-border p-3">
            <div className="gt-eyebrow mb-3">Kirim posisi manual</div>
            <div className="grid grid-cols-2 gap-2 md:grid-cols-6">
              <NSelect value={m.imei} onChange={(v) => setM({ ...m, imei: v })} className="col-span-2" testId="simulator-imei">{vehicles.map((v) => <option key={v.id} value={v.imei}>{v.plate} · {v.imei}</option>)}</NSelect>
              {["lat", "lng", "speed", "direction"].map((k) => <input key={k} value={m[k]} onChange={(e) => setM({ ...m, [k]: e.target.value })} placeholder={k} className="gt-input font-mono" data-testid={`simulator-${k}`} />)}
            </div>
            <div className="mt-3 flex items-center justify-between">
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={m.acc} onChange={(e) => setM({ ...m, acc: e.target.checked })} className="accent-yellow-500" data-testid="simulator-acc" /> ACC ON</label>
              <button onClick={send} className="flex items-center gap-1.5 rounded-xl bg-foreground px-3 py-2 text-xs font-bold text-background" data-testid="simulator-send"><Send className="h-3.5 w-3.5" /> Kirim</button>
            </div>
          </div>
          <div className="rounded-xl bg-background/60 p-3 font-mono text-[11px] leading-relaxed text-muted-foreground">
            POST {API_BASE}/ingest/glazy_json<br />Header X-Ingest-Token: &lt;INGEST_TOKEN di backend .env&gt;<br />{"{"}"imei":"…","lat":-6.2,"lng":106.8,"speed":40,"acc":true,"timestamp":"…"{"}"}
          </div>
        </div>
      )}
    </Card>
  );
}

export default function SettingsPage() {
  const { theme, setTheme } = useTheme();
  const { isAdmin } = useAuth();
  return (
    <div>
      <PageHeader eyebrow="Pengaturan" title="Pengaturan" subtitle="Aplikasi HP, notifikasi, tampilan, keamanan akun dan alat pengembangan." />
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
        <InstallAppCard />
        <PushCard />
        <Card title="Tampilan" icon={theme === "dark" ? Moon : Sun} testId="settings-theme-card">
          <div className="grid grid-cols-2 gap-3">
            {[["dark", "Dark", "Charcoal premium"], ["light", "Light", "Bersih & terang"]].map(([k, l, d]) => (
              <button key={k} onClick={() => setTheme(k)} data-testid={`settings-theme-${k}`}
                className={cn("rounded-2xl border p-4 text-left transition-colors", theme === k ? "border-primary bg-primary/10" : "border-border hover:border-primary/40")}>
                <div className={cn("mb-3 h-14 rounded-xl border border-border", k === "dark" ? "bg-[#0A0D12]" : "bg-[#F8FAFC]")}><div className="m-2 h-2 w-10 rounded bg-yellow-500" /></div>
                <div className="text-sm font-bold">{l}</div><div className="text-xs text-muted-foreground">{d}</div>
              </button>
            ))}
          </div>
        </Card>
        <PasswordCard />
        {!isAdmin && <EmailAlertCard />}
        {isAdmin && <div className="lg:col-span-2"><SimulatorCard /></div>}
      </div>
    </div>
  );
}
